import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import { useLocation } from 'react-router'
import { isRecord } from 'lib/object/objectUtils'
import { get as getSessionStorage, set as setSessionStorage } from 'lib/storage/isomorphicSessionStorage'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'

export type RightPanelView = 'map' | 'timeline' | 'list' | 'cart'
type PersistedRightPanelView = Exclude<RightPanelView, 'cart'>

const DEFAULT_RIGHT_PANEL_VIEW: PersistedRightPanelView = 'map'
const RIGHT_PANEL_VIEW_STORAGE_KEY = 'tripPlannerChatRightPanelView'
const CHECKOUT_RETURN_VIEW_HISTORY_STATE_KEY = 'tripChatRightPanelView'

interface UseRightPanelStateReturn {
  rightPanelView: RightPanelView
  setRightPanelView: Dispatch<SetStateAction<RightPanelView>>
  rememberRightPanelView: (view: RightPanelView) => void
  selectedDayItineraryId: number | undefined
  setSelectedDayItineraryId: Dispatch<SetStateAction<number | undefined>>
  selectedMarkerData: MarkerSelectionData | undefined
  handleMarkerSelect: (data: MarkerSelectionData | undefined) => void
  handleMarkerOverlayClose: () => void
}

function isPersistedRightPanelView(value: unknown): value is PersistedRightPanelView {
  return value === 'map' || value === 'timeline' || value === 'list'
}

function isStoredRightPanelView(value: unknown): value is RightPanelView {
  return isPersistedRightPanelView(value) || value === 'cart'
}

export function getTripChatCheckoutReturnView(): RightPanelView | undefined {
  if (typeof window === 'undefined') return undefined
  const historyState = window.history.state
  if (!isRecord(historyState)) return undefined
  const value = historyState[CHECKOUT_RETURN_VIEW_HISTORY_STATE_KEY]
  return isStoredRightPanelView(value) ? value : undefined
}

function getPersistedRightPanelView(): RightPanelView {
  const checkoutReturnView = getTripChatCheckoutReturnView()
  if (checkoutReturnView) return checkoutReturnView
  const value = getSessionStorage(RIGHT_PANEL_VIEW_STORAGE_KEY)
  return isStoredRightPanelView(value) ? value : DEFAULT_RIGHT_PANEL_VIEW
}

function resolveRightPanelViewAction(
  action: SetStateAction<RightPanelView>,
  currentView: RightPanelView,
): RightPanelView {
  return typeof action === 'function' ? action(currentView) : action
}

export function rememberTripChatRightPanelView(view: RightPanelView) {
  if (isPersistedRightPanelView(view)) {
    setSessionStorage(RIGHT_PANEL_VIEW_STORAGE_KEY, view)
  }
}

export function rememberTripChatCheckoutReturnView(view: RightPanelView) {
  if (isStoredRightPanelView(view) && typeof window !== 'undefined') {
    const historyState = window.history.state
    window.history.replaceState({
      ...(isRecord(historyState) ? historyState : {}),
      [CHECKOUT_RETURN_VIEW_HISTORY_STATE_KEY]: view,
    }, '', window.location.href)
  }
}

export function useRightPanelState(): UseRightPanelStateReturn {
  const { search } = useLocation()
  const [rightPanelView, setRightPanelViewState] = useState<RightPanelView>(() => {
    return new URLSearchParams(search).get('openCart') === 'true' ? 'cart' : getPersistedRightPanelView()
  })
  const [selectedDayItineraryId, setSelectedDayItineraryId] = useState<number | undefined>(undefined)
  const [selectedMarkerData, setSelectedMarkerData] = useState<MarkerSelectionData | undefined>(undefined)

  const rememberRightPanelView = useCallback((view: RightPanelView) => {
    rememberTripChatRightPanelView(view)
  }, [])

  const setRightPanelView = useCallback<Dispatch<SetStateAction<RightPanelView>>>((nextView) => {
    setRightPanelViewState(currentView => resolveRightPanelViewAction(nextView, currentView))
  }, [])

  const handleMarkerSelect = useCallback((data: MarkerSelectionData | undefined) => {
    setSelectedMarkerData(data)
  }, [])

  const handleMarkerOverlayClose = useCallback(() => {
    setSelectedMarkerData(undefined)
  }, [])

  return {
    rightPanelView,
    setRightPanelView,
    rememberRightPanelView,
    selectedDayItineraryId,
    setSelectedDayItineraryId,
    selectedMarkerData,
    handleMarkerSelect,
    handleMarkerOverlayClose,
  }
}
